import React, { ReactNode, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import classNames from 'classnames';

import * as errorHandlerActions from '@/shared/modules/errorHandler/actions';
import * as notificationActions from '@/shared/modules/notification/actions';
import { getErrorInfoDependsOfStatusCode } from '@/shared/modules/errorHandler/constants';

import Button from '@/shared/components/button/Button';

import type { ErrorType } from '@/shared/modules/errorHandler/types';

interface ErrorHandlerOwnProps {
	errorType: ErrorType;
	children: ReactNode;
}

interface ErrorHandlerStateProps {
	error: unknown;
	errorStatusCode: number | string;
	hasError: boolean;
	ignoreError: boolean;
	displayNotification: boolean;
	typeError: ErrorType;
	customNotification: { code: number; message: string } | null;
}

interface ErrorHandlerDispatchProps {
	clearError: typeof errorHandlerActions.clearError;
	createNotification: typeof notificationActions.createNotification;
}

type ErrorHandlerProps = ErrorHandlerOwnProps & ErrorHandlerStateProps & ErrorHandlerDispatchProps;

const ErrorHandler = ({
	error,
	createNotification,
	clearError,
	errorStatusCode,
	children,
	hasError,
	ignoreError,
	displayNotification,
	errorType,
	typeError,
	customNotification,
}: ErrorHandlerProps) => {
	const location = useLocation();
	const { t } = useTranslation();

	const shouldDisplayError = hasError && !displayNotification && !ignoreError && typeError === errorType;

	useEffect(() => {
		return () => {
			if (typeError === errorType) {
				clearError();
			}
		};
	}, [location, typeError, errorType, clearError]);

	useEffect(() => {
		if (!displayNotification) {
			return;
		}

		const notification =
			(customNotification && customNotification.code === errorStatusCode && customNotification.message) ||
			getErrorInfoDependsOfStatusCode(errorStatusCode, 'title', t);

		createNotification(notification, null, true, 5000, true);
		clearError();
	}, [clearError, createNotification, customNotification, displayNotification, errorStatusCode, t]);

	if (!shouldDisplayError) {
		return <>{children}</>;
	}

	const titleKey = getErrorInfoDependsOfStatusCode(errorStatusCode, 'title');
	const descriptionKey = getErrorInfoDependsOfStatusCode(errorStatusCode, 'description');
	const buttonKey = getErrorInfoDependsOfStatusCode(errorStatusCode, 'back_to_home');

	return (
		<div
			className={classNames('main page-wrap blocking-page', {
				'content-blocking': errorType === 'content-blocking',
			})}
		>
			<div className="error-page">
				<div
					style={{
						minHeight: '100vh',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '24px',
					}}
				>
					<div
						style={{
							maxWidth: '520px',
							textAlign: 'center',
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						<h1>{t(titleKey)}</h1>
						<div>
							<Trans
								i18nKey={descriptionKey}
								components={[
									<a key="support" className="link" href="mailto:klantenservice@squla.nl" />,
								]}
							/>
						</div>
						<div>
							<Button
								title={t(buttonKey)}
								onClick={() => {
									clearError();
									window.location.reload();
								}}
							/>
						</div>
						{import.meta.env.DEV && error ? (
							<pre
								style={{
									textAlign: 'left',
									whiteSpace: 'pre-wrap',
									overflowWrap: 'anywhere',
								}}
							>
								{String((error as Error)?.message || error)}
							</pre>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
};

function mapStateToProps({ errorHandlerReducer }: any) {
	return {
		error: errorHandlerReducer.error,
		errorStatusCode: errorHandlerReducer.statusCode,
		hasError: errorHandlerReducer.hasError,
		ignoreError: errorHandlerReducer.ignoreError,
		displayNotification: errorHandlerReducer.displayNotification,
		typeError: errorHandlerReducer.typeError,
		customNotification: errorHandlerReducer.customNotification,
	};
}

const mapDispatchToProps = {
	clearError: errorHandlerActions.clearError,
	createNotification: notificationActions.createNotification,
};

export default connect(mapStateToProps, mapDispatchToProps)(ErrorHandler);
